// ============================================================================
// NIGHT AUDIT SERVICE (PMS Phase 2 slice 6a)
// ============================================================================
// crs.Api.Controllers.NightAuditModule.NightAuditController -> /api/v1/NightAudit

import { get, post, put } from '../client/axiosClient';
import { buildEndpoint, buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { ApiResponse } from '../types';
import type {
  NightAuditPreview,
  NightAuditRun,
  NightAuditRunRequest,
  NightAuditRunResult,
  NightAuditStatus,
} from '../types/nightAudit.types';

const BASE = '/NightAudit';

const unwrap = <T,>(res: { data?: ApiResponse<T> }, fallback: string): T => {
  if (res.data?.data === undefined || res.data?.data === null) throw new Error(res.data?.message || fallback);
  return res.data.data;
};

class NightAuditService {
  async status(propertyId: string): Promise<NightAuditStatus> {
    return unwrap(await get<ApiResponse<NightAuditStatus>>(buildEndpointWithQuery(`${BASE}/status`, { propertyId })),
      'Night audit status unavailable.');
  }

  async preview(propertyId: string, auditDate?: string): Promise<NightAuditPreview> {
    return unwrap(await get<ApiResponse<NightAuditPreview>>(
      buildEndpointWithQuery(`${BASE}/preview`, { propertyId, auditDate })), 'Preview failed.');
  }

  async run(request: NightAuditRunRequest): Promise<NightAuditRunResult> {
    return unwrap(await post<ApiResponse<NightAuditRunResult>>(buildEndpoint(`${BASE}/run`), request), 'The run did not answer.');
  }

  /** Sets the night audit start date (submit) — refused once an audit has completed. */
  async setStartDate(propertyId: string, startDate: string): Promise<NightAuditStatus> {
    return unwrap(await put<ApiResponse<NightAuditStatus>>(buildEndpoint(`${BASE}/start-date`), { propertyId, startDate }),
      'The start date was not saved.');
  }

  async history(propertyId: string, take = 31): Promise<NightAuditRun[]> {
    const res = await get<ApiResponse<NightAuditRun[]>>(buildEndpointWithQuery(`${BASE}/history`, { propertyId, take }));
    return res.data?.data ?? [];
  }
}

export const nightAuditService = new NightAuditService();
export default nightAuditService;
